/* eslint-disable max-len */
import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { dateManipulateDay, dateManipulateMonth, diffDays, endOfMonth, format, nowDate, secToHour } from '../../../utils/date'
import Holidays from 'date-holidays'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { clone } from 'lodash'
import GanttChartScheduleList from './GanttChartScheduleList'
import LoadingSquare from '../common/LoadingSquare'
import Tips from '../common/Tips'
import { useSelector } from 'react-redux'
import { RootState } from '../../../states/containers/store'
import { convertHexColor } from '../../../utils/color'
import { PackageBlock, ResponseGetTaskGanttChart } from '../../../states/types/task'
import SELECT_LIST from '../../constants/taskGanttChartStatus'

interface LabelGroup {
  id: number
  name: string
  color: string
  schedule: {
    start_date: string | null
    end_date: string | null
    time: number
  }
  actual: {
    date: string
    time: number
  }[]
}

interface LabelGroupCategory extends LabelGroup {
  parent_id: number | null
  label_groups: {
    id: number
    name: string
    code: string | null
    color: string
  }[]
}

interface Props {
  labelData: LabelGroup | LabelGroupCategory
  detailDataList: ResponseGetTaskGanttChart | undefined
  fetchCallback: (blockType: string, id: string, callback: VoidFunction) => void
  updateCallback: (blockType: string, id: number, startDate: string, endDate: string, callback: VoidFunction, assignedMemberIds?: number[], parentId?: number) => void
  sortCallback: (parent: {blockType: string, id: number}, children: {blockType: string, id: number, label_master_id?: number}[], callback: VoidFunction) => void
  openModalCallback: (params: {blockType: string, id: number, modalType: string, displayType: string, labelGroupId?: number, parentId?: number, assignedMemberIds?: number[], bgColor?: string}) => void
  displayBlockType: string
  open?: boolean
}

const GanttChart: FC<Props> = (props) => {
  const {
    labelData,
    detailDataList,
    fetchCallback,
    updateCallback,
    sortCallback,
    openModalCallback,
    displayBlockType,
    open,
  } = props

  const {
    current,
    login,
  } = useSelector((state: RootState) => state)

  const holidays = new Holidays()
  holidays.init('JP')

  const DAILY_BLOCK_WIDTH = 22
  const WEEKLY_BLOCK_WIDTH = (DAILY_BLOCK_WIDTH * 3) + 2

  const CHARGE_WIDTH = 100

  const titleRef = useRef() as React.MutableRefObject<HTMLDivElement>

  const [isOpen, setIsOpen] = useState<boolean>(open || false)

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const [dragStartData, setDragStartData] = useState<{blockType: string, id: number, index: number, parent: {blockType: string, id: number}} | undefined>()
  const [dragEndData, setDragEndData] = useState<{blockType: string, id: number, index: number, parent: {blockType: string, id: number}} | undefined>()

  const [dataList, setDataList] = useState<ResponseGetTaskGanttChart>()

  const [sortParams, setSortParams] = useState<{parent: {blockType: string, id: number}, children: {blockType: string, id: number, label_master_id?: number}[]}>()

  const [isDisplayWeekly, setIsDisplayWeekly] = useState<boolean>(false)

  const [addMonthNum, setAddMonthNum] = useState<number>(0)

  const [titleWidth, setTitleWidth] = useState<number>(250)
  const [isMoveDrag, setIsMoveDrag] = useState<boolean>(false)

  const [isDisplayStatus, setIsDisplayStatus] = useState<boolean>(true)
  const [statusWidth, setStatusWidth] = useState<number>(100)

  const displayDateList = useMemo(() => {
    if (!detailDataList) return []
    const targetDate = dateManipulateMonth(nowDate("YYYY-MM-01"), addMonthNum)
    if (isDisplayWeekly) {
      const weekList = []
      const startDate = dateManipulateMonth(targetDate, -1)
      const endDate = dateManipulateMonth(targetDate, 3)
      for (let d = clone(new Date(startDate));d <= new Date(endDate);d.setDate(d.getDate() + 1)) {
        if (d.getDay() === 1) {
          weekList.push(format(d, "YYYY-MM-DD"))
        }
      }
      return weekList
    }
    const dateList = []
    const startDate = dateManipulateMonth(targetDate, -1)
    const endDate = endOfMonth(dateManipulateMonth(targetDate, 1))
    for (let d = clone(new Date(startDate));d <= new Date(endDate);d.setDate(d.getDate() + 1)) {
      dateList.push(format(d, "YYYY-MM-DD"))
    }
    return Array.from(new Set(dateList))
  }, [detailDataList, isDisplayWeekly, addMonthNum])

  const listCount = useMemo(() => {
    let count = 1
    const counter = (children: PackageBlock[]) => {
      children.forEach((v) => {
        count += 1
        counter(v.children || [])
      })
    }
    counter(detailDataList?.children || [])
    return count
  }, [detailDataList])

  const calendarColor = (day: string, bg = false) => {
    if (Number.isNaN(new Date(day).getTime())) return bg ? "#FFFFFF" : "#666666"
    const holiday = holidays.isHoliday(new Date(day))
    if ((holiday && holiday[0].type === "public") || format(day, 'd') === '0') {
      return bg ? "#fff7f7" : "#FF0000"
    }
    if (format(day, 'd') === '6') {
      return bg ? "#f1f9ff" : "#317AB7"
    }
    return bg ? "#FFFFFF" : "#666666"
  }

  const buildSecToTime = (sec: number) => {
    const hour = Number(secToHour(sec)) ? `${secToHour(sec)}時間` : ""
    return `${hour}`
  }

  const buildBgColor = (parent: {blockType: string | undefined, id: number | undefined}, color?: string, blockType?: string) => {
    if (!dragStartData || (dragStartData.parent?.blockType === parent?.blockType && dragStartData.parent?.id === parent?.id)) {
      const alpha = blockType === "label" ? 0.05 : 0.2
      return convertHexColor(color || "#FFFFFF", alpha)
    }
    return "#EEEEEE"
  }

  const buildScheduleList = (values: PackageBlock[], parent?: {blockType: string, id: number | undefined, color: string}, bgColor?: string):JSX.Element => {
    if (!values.length) return (<></>)
    return (
      <>
        {
          values.map((v) => (
            <React.Fragment key={`scheduleList_${v.block_type}_${v.id}`}>
              <GanttChartScheduleList
                packageBlock={v}
                displayDateList={displayDateList}
                updateCallback={
                  (blockType, id, startDate, endDate, callback) => updateCallback(blockType, id, startDate, endDate, callback, v.assigned_members.map((w) => w.member_id), parent?.id)
                }
                openModalCallback={(blockType, id) => openModalCallback({
                  blockType: blockType,
                  id: id,
                  modalType: "config",
                  displayType: isDisplayWeekly ? "weekly" : "daily",
                  labelGroupId: v.block_type === "label" ? parent?.id : undefined,
                  assignedMemberIds: v.block_type === "label" ? v.assigned_members.map((w) => w.member_id) : undefined,
                })}
                displayType={isDisplayWeekly ? "weekly" : "daily"}
                displayBlockWidth={isDisplayWeekly ? WEEKLY_BLOCK_WIDTH : DAILY_BLOCK_WIDTH}
                bgColor={buildBgColor({ blockType: parent?.blockType, id: parent?.id }, bgColor || v.color, v.block_type)}
                editable={login.role_permission?.task_project_setting}
              />
              {buildScheduleList(v.children || [], { blockType: v.block_type, id: v.id, color: v.color }, v.block_type === "label_group" ? v.color : "")}
            </React.Fragment>
          ))
        }
      </>
    )
  }

  const buildIcon = (blockType: string) => {
    if (blockType === "label_group_category") return "folder"
    if (blockType === "label_group") return "square"
    if (blockType === "label") return "circle"
    return "circle"
  }

  const buildDisplayType = (blockType: string) => {
    if (blockType === "label_group_category") return "カテゴリー"
    if (blockType === "label_group") return "プロジェクト"
    if (blockType === "label") return "業務"
    return ""
  }

  const rebuildChildren = (value: ResponseGetTaskGanttChart | PackageBlock):ResponseGetTaskGanttChart | PackageBlock => {
    if (!value || !dragStartData || !dragEndData) return value
    if (value.block_type === dragStartData.parent.blockType && value.id === dragStartData.parent.id) {
      const tmpArray:PackageBlock[] = []
      if (dragStartData.index > dragEndData.index) {
        value.children.forEach((_w, i) => {
          if (i === dragEndData.index) {
            tmpArray.push(value.children[dragStartData.index])
          }
          if (i !== dragStartData.index) {
            tmpArray.push(value.children[i])
          }
        })
      } else {
        value.children.forEach((_w, i) => {
          if (i !== dragStartData.index) {
            tmpArray.push(value.children[i])
          }
          if (i === dragEndData.index) {
            tmpArray.push(value.children[dragStartData.index])
          }
        })
      }
      setSortParams({
        parent: { blockType: dragStartData.parent.blockType, id: dragStartData.parent.id },
        children: tmpArray.map((v) => ({ blockType: v.block_type, id: v.id, label_master_id: v.label_master_id })),
      })
      return { ...value, children: tmpArray }
    }
    // eslint-disable-next-line no-use-before-define
    return { ...value, children: rebuildDataList(value.children) as PackageBlock[] }
  }

  const rebuildDataList = (values: ResponseGetTaskGanttChart | PackageBlock[] | undefined):ResponseGetTaskGanttChart | PackageBlock[] => {
    if (!values || !dragStartData || !dragEndData) return []
    if (Array.isArray(values)) {
      return values.map((v:PackageBlock):PackageBlock => rebuildChildren(v))
    }
    return rebuildChildren(values) as ResponseGetTaskGanttChart
  }

  const dragStart = (value: PackageBlock, parent: {blockType: string, id: number}, index: number) => {
    setDragStartData({
      blockType: value.block_type,
      id: value.id,
      index: index,
      parent: parent,
    })
  }

  const dragEnd = (value: PackageBlock, parent: {blockType: string, id: number}, index: number) => {
    if (!dragStartData?.parent || !parent || !dataList) {
      setDragStartData(undefined)
      setDragEndData(undefined)
      return
    }
    if (dragStartData.parent.blockType === parent.blockType && dragStartData.parent.id === parent.id) {
      setDragEndData({
        blockType: value.block_type,
        id: value.id,
        index: index,
        parent: parent,
      })
    } else {
      setDragStartData(undefined)
      setDragEndData(undefined)
    }
  }

  const buildTitleList = (level = 0, values: PackageBlock[], rules?: boolean[], noRule?: boolean, parent?: {blockType: string, id: number}, bgColor?: string):JSX.Element => {
    if (!values.length) return (<></>)
    return (
      <>
        {
          values.map((v, i) => {
            const ruleList = [...rules?.map((rule, index) => (rules.length === index + 1 ? !rule : rule)) || [], values.length === i + 1]
            return (
              <React.Fragment key={`title_${v.block_type}_${v.id}`}>
                <div
                  style={{
                    position: "relative",
                    // eslint-disable-next-line no-nested-ternary
                    cursor: (level && login.role_permission?.task_project_setting) ? dragStartData?.blockType === v.block_type && dragStartData.id === v.id ? "move" : "row-resize" : "initial",
                    backgroundColor: buildBgColor({ blockType: parent?.blockType, id: parent?.id }, bgColor || v.color, v.block_type),
                  }}
                >
                  <div
                    style={{
                      position: "relative",
                      marginTop: "-1px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      border: "1px solid #cccccc",
                      padding: "4px 8px",
                      minHeight: "40px",
                      userSelect: "none",
                      opacity: !dragStartData || (dragStartData.parent?.blockType === parent?.blockType && dragStartData.parent?.id === parent?.id) ? "1" : "0.5",
                    }}
                    draggable={login.role_permission?.task_project_setting}
                    onDragStart={() => login.role_permission?.task_project_setting && parent && dragStart(v, parent, i)}
                    onDragOver={(e) => e.preventDefault()}
                    onMouseUp={() => parent && dragEnd(v, parent, i)}
                    onDrop={() => parent && dragEnd(v, parent, i)}
                    role="none"
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        maxWidth: `${titleWidth - 90}px`,
                      }}
                    >
                      {
                        !!(level && login.role_permission?.task_project_setting) && (
                          <FontAwesomeIcon
                            icon={['fas', 'grip-vertical']}
                            className=""
                            style={{
                              color: "#999999",
                            }}
                          />
                        )
                      }
                      {
                        ruleList.map((w, j) => {
                          if (noRule) return (<React.Fragment key={`${v.name}_${j + 0}_${level}`} />)
                          return (
                            <div
                              key={`${v.name}_${j + 0}_${level}`}
                              style={{
                                minWidth: "14px",
                              }}
                            >
                              {
                                ruleList.length === j + 1 ? (
                                  <>
                                    {w ? (<>┗</>) : (<>┣</>)}
                                  </>
                                ) : (
                                  <>
                                    {w && (<>┃</>)}
                                  </>
                                )
                              }
                            </div>
                          )
                        })
                      }
                      <div>
                        <div
                          style={{
                            fontSize: "8px",
                            marginBottom: "2px",
                            color: "#666666",
                            fontWeight: "bold",
                          }}
                        >
                          {buildDisplayType(v.block_type)}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <FontAwesomeIcon
                            icon={['fas', buildIcon(v.block_type)]}
                            className="icon"
                            style={{
                              color: v.color,
                              fontSize: "14px",
                            }}
                          />
                          <div
                            title={v.name}
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: `${titleWidth - 130}px`,
                            }}
                          >
                            {v.name}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "4px",
                        alignItems: "center",
                      }}
                    >
                      {
                        v.block_type === "label" && (
                          <Tips
                            description="詳細情報"
                            className=""
                            icon={false}
                          >
                            <>
                              <button
                                type="button"
                                style={{
                                  border: "solid 1px #CCCCCC",
                                  borderRadius: "4px",
                                  padding: "4px",
                                  color: "#666666",
                                  backgroundColor: "#FFFFFF",
                                }}
                                onClick={() => openModalCallback({
                                  blockType: v.block_type,
                                  id: v.id,
                                  modalType: "detail",
                                  displayType: isDisplayWeekly ? "weekly" : "daily",
                                  labelGroupId: v.block_type === "label" ? parent?.id : undefined,
                                  assignedMemberIds: v.block_type === "label" ? v.assigned_members.map((w) => w.member_id) : undefined,
                                  bgColor: buildBgColor({ blockType: parent?.blockType, id: parent?.id }, bgColor || v.color, v.block_type),
                                })}
                              >
                                <FontAwesomeIcon
                                  icon={['fas', 'users']}
                                  className=""
                                  fixedWidth
                                />
                              </button>
                            </>
                          </Tips>
                        )
                      }
                      {
                        login.role_permission?.task_project_setting && (
                          <Tips
                            description="設定"
                            className=""
                            icon={false}
                          >
                            <>
                              <button
                                type="button"
                                style={{
                                  border: "solid 1px #CCCCCC",
                                  borderRadius: "4px",
                                  padding: "4px",
                                  color: "#666666",
                                  backgroundColor: "#FFFFFF",
                                }}
                                onClick={() => openModalCallback({
                                  blockType: v.block_type,
                                  id: v.id,
                                  modalType: "config",
                                  displayType: isDisplayWeekly ? "weekly" : "daily",
                                  labelGroupId: v.block_type === "label" ? parent?.id : undefined,
                                  assignedMemberIds: v.block_type === "label" ? v.assigned_members.map((w) => w.member_id) : undefined,
                                })}
                              >
                                <FontAwesomeIcon
                                  icon={['fas', 'cog']}
                                  className=""
                                  fixedWidth
                                />
                              </button>
                            </>
                          </Tips>
                        )
                      }
                    </div>
                  </div>
                  {buildTitleList(level + 1, v.children || [], ruleList, false, { blockType: v.block_type, id: v.id }, v.block_type === "label_group" ? v.color : "")}
                </div>
              </React.Fragment>
            )
          })
        }
      </>
    )
  }

  const buildCostList = (values: PackageBlock[]):JSX.Element => {
    if (!values.length) return (<></>)
    return (
      <>
        {
          values.map((v) => (
            <React.Fragment key={`cost_${v.block_type}_${v.id}`}>
              <div
                style={{
                  display: "flex",
                  flexFlow: "column",
                  gap: "4px",
                  padding: "2px 4px",
                  height: "40px",
                  marginTop: "-1px",
                  border: "1px solid #dddddd",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "10px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "10px",
                      borderRadius: "4px",
                      backgroundColor: "#6fcdcd",
                      color: "#FFFFFF",
                      padding: "2px 4px",
                    }}
                  >
                    予定工数
                  </div>
                  <div
                    style={{
                      color: "#666666",
                    }}
                  >
                    {buildSecToTime(v.schedule.time || 0)}
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "10px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "10px",
                      borderRadius: "4px",
                      backgroundColor: "#cc6868",
                      color: "#FFFFFF",
                      padding: "2px 4px",
                    }}
                  >
                    実績工数
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      color: "#666666",
                    }}
                  >
                    {buildSecToTime(v.actual.reduce((s, x) => s + x.time, 0) || 0)}
                    {
                      !!v.schedule.time && (v.actual.reduce((s, x) => s + x.time, 0) || 0) - v.schedule.time >= 3600 && (
                        <Tips
                          description={`予定より+${buildSecToTime((v.actual.reduce((s, x) => s + x.time, 0) || 0) - v.schedule.time)}`}
                          className=""
                          icon={false}
                        >
                          <FontAwesomeIcon
                            icon={['fas', 'exclamation-circle']}
                            className=""
                            fixedWidth
                            style={{
                              color: "#FF0000",
                              fontSize: "12px",
                            }}
                          />
                        </Tips>
                      )
                    }
                  </div>
                </div>
              </div>
              {buildCostList(v.children || [])}
            </React.Fragment>
          ))
        }
      </>
    )
  }

  const buildChargeList = (values: PackageBlock[]):JSX.Element => {
    if (!values.length) return (<></>)
    return (
      <>
        {
          values.map((v) => (
            <React.Fragment key={`charge_${v.block_type}_${v.id}`}>
              <div
                style={{
                  position: "relative",
                  marginTop: "-1px",
                  display: "flex",
                  alignItems: "center",
                  gap: "2px",
                  padding: "2px 8px",
                  minHeight: "40px",
                  userSelect: "none",
                  border: "1px solid #CCCCCC",
                  backgroundColor: v.block_type === "label" ? "#FFFFFF" : "#EEEEEE",
                }}
              >
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    userSelect: "none",
                  }}
                >
                  {
                    v.block_type === "label" ? (
                      <>
                        {
                          v.assigned_members.length ? (
                            <>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  paddingLeft: "10px",
                                }}
                              >
                                {
                                  v.assigned_members.map((member, i) => {
                                    if (i >= 4) return (<React.Fragment key={`assigned_member_${member.member_id}_${v.id}`} />)
                                    return (
                                      <Tips
                                        description={`担当者:${member.name}`}
                                        className=""
                                        icon={false}
                                        key={`assigned_member_${member.member_id}_${v.id}`}
                                      >
                                        {
                                          member.profile_image ? (
                                            <img
                                              alt=""
                                              src={member.profile_image}
                                              className="profile_icon"
                                              style={{
                                                width: "20px",
                                                height: "20px",
                                                borderRadius: "50%",
                                                border: "solid 1px #FFFFFF",
                                                marginLeft: "-10px",
                                              }}
                                            />
                                          ) : (
                                            <img
                                              alt=""
                                              src={`/static/images/profile/${(member.member_id || 0) % 12}.jpeg`}
                                              className="profile_icon"
                                              style={{
                                                width: "20px",
                                                height: "20px",
                                                borderRadius: "50%",
                                                border: "solid 1px #FFFFFF",
                                                marginLeft: "-10px",
                                              }}
                                            />
                                          )
                                        }
                                      </Tips>
                                    )
                                  })
                                }
                                {
                                  v.assigned_members.length > 4 && (
                                    <div
                                      style={{
                                        fontSize: "9px",
                                        color: "#999999",
                                      }}
                                    >
                                      {`他${v.assigned_members.length - 4}名`}
                                    </div>
                                  )
                                }
                              </div>
                            </>
                          ) : (
                            <>
                              <div
                                style={{
                                  fontSize: "10px",
                                  color: "#999999",
                                  textAlign: "center",
                                  width: "100%",
                                }}
                              >
                                未設定
                              </div>
                            </>
                          )
                        }
                      </>
                    ) : (
                      <>
                        <div
                          style={{
                            fontSize: "10px",
                            color: "#999999",
                            textAlign: "center",
                            width: "100%",
                          }}
                        >
                          <Tips
                            description="担当者は業務のみ設定することができます"
                            className=""
                          >
                            <>
                              設定不可
                            </>
                          </Tips>
                        </div>
                      </>
                    )
                  }
                </div>
              </div>
              {buildChargeList(v.children || [])}
            </React.Fragment>
          ))
        }
      </>
    )
  }

  const buildStatusList = (values: PackageBlock[]):JSX.Element => {
    if (!values.length) return (<></>)
    return (
      <>
        {
          values.map((v) => {
            const targetStatus = SELECT_LIST.find((w) => w.value === v.status)
            return (
              <React.Fragment key={`status_${v.block_type}_${v.id}`}>
                <div
                  style={{
                    position: "relative",
                    marginTop: "-1px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    border: "1px solid #cccccc",
                    padding: "4px 8px",
                    minHeight: "40px",
                    backgroundColor: "#FFFFFF",
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                      textAlign: "center",
                      borderRadius: "4px",
                      padding: "4px",
                      appearance: "auto",
                      backgroundColor: targetStatus?.bg || "#FFFFFF",
                    }}
                  >
                    {targetStatus?.indexLabel || "-"}
                  </div>
                </div>
                {buildStatusList(v.children || [])}
              </React.Fragment>
            )
          })
        }
      </>
    )
  }

  const buildYMD = (date: string) => {
    if (!date) return (<></>)
    return (
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: "1px",
          fontSize: "10px",
          color: "#333333",
        }}
      >
        <div
          style={{
            fontSize: "14px",
            fontWeight: "bold",
          }}
        >
          {format(date, "YYYY")}
        </div>
        年
        <div
          style={{
            fontSize: "14px",
            fontWeight: "bold",
          }}
        >
          {format(date, "M")}
        </div>
        月
        <div
          style={{
            fontSize: "14px",
            fontWeight: "bold",
          }}
        >
          {format(date, "D")}
        </div>
        日
      </div>
    )
  }

  const todayBorder = () => {
    let startPosition = 0
    if (
      !(
        Number(format(displayDateList[0], "YYYYMMDD")) < Number(nowDate("YYYYMMDD"))
        && Number(format(displayDateList[displayDateList.length - 1], "YYYYMMDD")) > Number(nowDate("YYYYMMDD"))
      )
    ) return (<></>)
    if (!isDisplayWeekly) {
      startPosition = diffDays(displayDateList[0], nowDate()) * (DAILY_BLOCK_WIDTH + 1)
    } else {
      startPosition = (diffDays(displayDateList[0], nowDate()) / 7) * (WEEKLY_BLOCK_WIDTH + 1)
    }
    return (
      <>
        <div
          style={{
            position: "absolute",
            top: 0,
            left: `${startPosition}px`,
            width: "1px",
            height: `${listCount * 39}px`,
            backgroundColor: "#FF0000",
            zIndex: 1,
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "-14px",
            left: `${startPosition + 0.5}px`,
            zIndex: 1,
            fontSize: "16px",
            transform: "translateX(-50%)",
            color: "#FF0000",
          }}
        >
          <Tips
            description="本日"
            className="bottom"
            icon={false}
          >
            <>
              <FontAwesomeIcon icon={['fas', 'caret-down']} />
            </>
          </Tips>
        </div>
      </>
    )
  }

  useEffect(() => {
    if (!isOpen) return
    setIsLoading(true)
    setTitleWidth(250)
    fetchCallback(
      displayBlockType,
      labelData.id.toString(),
      () => {
        setIsLoading(false)
      },
    )
  }, [isOpen])

  useEffect(() => {
    if (!detailDataList) return
    setDataList(detailDataList)
  }, [detailDataList])

  useEffect(() => {
    if (!dragEndData) return
    setDataList(rebuildDataList(dataList) as ResponseGetTaskGanttChart)
    setDragStartData(undefined)
    setDragEndData(undefined)
  }, [dragEndData])

  useEffect(() => {
    if (!sortParams) return
    sortCallback(
      sortParams.parent,
      sortParams.children,
      () => {
        setSortParams(undefined)
      },
    )
  }, [sortParams])

  useEffect(() => {
    setIsDisplayWeekly(current.taskGanttChartRangeTypeInit === "weekly")
  }, [])

  return (
    <>
      <div>
        <div
          key={`gantt_chart_area_wrapper_${labelData.id}_${labelData.color}`}
          style={{
            border: "solid 1px #CCCCCC",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px",
            backgroundColor: "#efefef",
            minHeight: "40px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              width: "calc(100% - 140px)",
            }}
            onClick={() => setIsOpen(!isOpen)}
            role="none"
          >
            <FontAwesomeIcon icon={['fas', isOpen ? 'angle-down' : 'angle-right']} />
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <FontAwesomeIcon
                icon={['fas', buildIcon(displayBlockType)]}
                className="icon"
                style={{
                  color: labelData.color,
                  fontSize: "14px",
                }}
              />
              {labelData.name}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <div
                style={{
                  backgroundColor: "#219c0a",
                  color: "#FFFFFF",
                  borderRadius: "4px",
                  fontSize: "9px",
                  padding: "2px 4px",
                }}
              >
                予定期間
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: "4px",
                }}
              >
                {
                  labelData.schedule.start_date ? (
                    <>
                      {buildYMD(labelData.schedule.start_date)}
                    </>
                  ) : (
                    <>
                      指定なし
                    </>
                  )
                }
                ~
                {
                  labelData.schedule.end_date ? (
                    <>
                      {buildYMD(labelData.schedule.end_date)}
                    </>
                  ) : (
                    <>
                      指定なし
                    </>
                  )
                }
              </div>
              {
                !!labelData.schedule.time && (
                  <>
                    <div
                      style={{
                        backgroundColor: "#219c0a",
                        color: "#FFFFFF",
                        borderRadius: "4px",
                        fontSize: "9px",
                        padding: "2px 4px",
                      }}
                    >
                      予定工数
                    </div>
                    <div>
                      {buildSecToTime(labelData.schedule.time)}
                    </div>
                  </>
                )
              }
            </div>
            <button
              type="button"
              style={{
                border: "1px solid #cccccc",
                borderRadius: "4px",
                padding: "4px 6px",
                display: "flex",
                gap: "4px",
                fontSize: "12px",
                backgroundColor: "#FFFFFF",
              }}
            >
              {
                isOpen ? (
                  <>
                    閉じる
                  </>
                ) : (
                  <>
                    開く
                  </>
                )
              }
            </button>
          </div>
          {
            isOpen && (
              <div
                className="button_area"
                key={`gantt_chart_switch_${labelData.id}_${labelData.color}_${isDisplayWeekly}`}
                style={{
                  width: "140px",
                }}
              >
                {
                  isDisplayWeekly ? (
                    <>
                      <Tips
                        description="日表示に切り替えます"
                        className=""
                        icon={false}
                      >
                        <>
                          <button
                            type="button"
                            onClick={() => setIsDisplayWeekly(false)}
                            style={{
                              border: "1px solid #cccccc",
                              borderRadius: "4px",
                              padding: "4px 6px",
                              display: "flex",
                              gap: "4px",
                              fontSize: "12px",
                              backgroundColor: "#FFFFFF",
                            }}
                          >
                            <FontAwesomeIcon icon={['fas', 'calendar-week']} />
                            週表示
                            <FontAwesomeIcon icon={['fas', 'arrow-right']} />
                            <FontAwesomeIcon icon={['fas', 'calendar-day']} />
                            日表示
                          </button>
                        </>
                      </Tips>
                    </>
                  ) : (
                    <>
                      <Tips
                        description="週表示に切り替えます"
                        className=""
                        icon={false}
                      >
                        <>
                          <button
                            type="button"
                            onClick={() => setIsDisplayWeekly(true)}
                            style={{
                              border: "1px solid #cccccc",
                              borderRadius: "4px",
                              padding: "4px 6px",
                              display: "flex",
                              gap: "4px",
                              fontSize: "12px",
                              backgroundColor: "#FFFFFF",
                            }}
                          >
                            <FontAwesomeIcon icon={['fas', 'calendar-day']} />
                            日表示
                            <FontAwesomeIcon icon={['fas', 'arrow-right']} />
                            <FontAwesomeIcon icon={['fas', 'calendar-week']} />
                            週表示
                          </button>
                        </>
                      </Tips>
                    </>
                  )
                }
              </div>
            )
          }
        </div>
        {
          isOpen && (
            <>
              <div
                style={{
                  border: "solid 1px #CCCCCC",
                  marginTop: "-1px",
                  padding: "8px",
                }}
              >
                {
                  isLoading ? (
                    <>
                      <LoadingSquare>
                        <>
                          ガントチャートを生成しています…
                        </>
                      </LoadingSquare>
                    </>
                  ) : (
                    <>
                      {
                        dataList ? (
                          <React.Fragment key={`gantt_chart_area_${dataList.block_type}_${dataList.id}_${isDisplayWeekly}`}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "8px",
                                backgroundColor: "#EEEEEE",
                                padding: "4px",
                              }}
                            >
                              <button
                                type="button"
                                style={{
                                  border: "1px solid #CCCCCC",
                                  borderRadius: "4px",
                                  padding: "4px 6px",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  fontSize: "12px",
                                  backgroundColor: "#ffffff",
                                }}
                                onClick={() => setAddMonthNum(addMonthNum - 1)}
                              >
                                <FontAwesomeIcon icon={['fas', 'caret-left']} />
                                1ヶ月前
                              </button>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "baseline",
                                  gap: "4px",
                                }}
                              >
                                表示期間:
                                {buildYMD(displayDateList[0])}
                                ~
                                {
                                  isDisplayWeekly ? (
                                    <>
                                      {buildYMD(dateManipulateDay(displayDateList[displayDateList.length - 1], 6))}
                                    </>
                                  ) : (
                                    <>
                                      {buildYMD(displayDateList[displayDateList.length - 1])}
                                    </>
                                  )
                                }
                              </div>
                              <button
                                type="button"
                                style={{
                                  border: "1px solid #CCCCCC",
                                  borderRadius: "4px",
                                  padding: "4px 6px",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  fontSize: "12px",
                                  backgroundColor: "#ffffff",
                                }}
                                onClick={() => setAddMonthNum(addMonthNum + 1)}
                              >
                                1ヶ月後
                                <FontAwesomeIcon icon={['fas', 'caret-right']} />
                              </button>
                            </div>
                            {
                              !dataList?.editable && !login.role_permission?.task_project_setting && (
                                <div
                                  style={{
                                    backgroundColor: "#ffdfdf",
                                    padding: "8px",
                                    color: "#c20000",
                                    margin: "8px 0",
                                  }}
                                >
                                  編集権限がないため、閲覧モードでの表示になります。また、実績は自身の実績のみ表示されます。
                                </div>
                              )
                            }
                            {
                              !dataList?.editable && login.role_permission?.task_project_setting && (
                                <div
                                  style={{
                                    backgroundColor: "#ffdfdf",
                                    padding: "8px",
                                    color: "#c20000",
                                    margin: "8px 0",
                                  }}
                                >
                                  表示権限により、実績は自身の実績のみ表示されます。
                                </div>
                              )
                            }
                            {
                              dataList?.editable && !login.role_permission?.task_project_setting && (
                                <div
                                  style={{
                                    backgroundColor: "#ffdfdf",
                                    padding: "8px",
                                    color: "#c20000",
                                    margin: "8px 0",
                                  }}
                                >
                                  編集権限がないため、閲覧モードでの表示になります。
                                </div>
                              )
                            }
                            <div
                              style={{
                                display: "flex",
                                position: "relative",
                              }}
                            >
                              <div
                                ref={titleRef}
                                style={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  width: "100%",
                                  height: "40px",
                                  zIndex: 1,
                                }}
                                onMouseMove={(e) => isMoveDrag && setTitleWidth(e.clientX - titleRef.current.getBoundingClientRect().left)}
                                onMouseUp={() => setIsMoveDrag(false)}
                                onMouseLeave={() => setIsMoveDrag(false)}
                                role="none"
                              >
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "12px",
                                    left: `${titleWidth}px`,
                                    cursor: "col-resize",
                                    transform: "translateX(-50%)",
                                    fontSize: "16px",
                                    color: "#EEEEEE",
                                  }}
                                  onMouseDown={() => setIsMoveDrag(true)}
                                  role="none"
                                >
                                  <FontAwesomeIcon icon={['fas', 'grip-lines-vertical']} />
                                </div>
                              </div>
                              {
                                !!dragEndData && (
                                  <div
                                    style={{
                                      position: "absolute",
                                      width: "100%",
                                      height: "100%",
                                      backgroundColor: "rgba(0, 0, 0, 0.2)",
                                      zIndex: "5",
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                      fontSize: "60px",
                                      color: "#FFFFFF",
                                    }}
                                  >
                                    <FontAwesomeIcon icon={['fas', 'spinner']} spin />
                                  </div>
                                )
                              }
                              <div
                                style={{
                                  width: `${titleWidth}px`,
                                }}
                              >
                                <div
                                  style={{
                                    padding: "4px",
                                    textAlign: "center",
                                    fontWeight: "bold",
                                    minHeight: "40px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    userSelect: "none",
                                  }}
                                >
                                  項目
                                </div>
                                {buildTitleList(0, [{ ...dataList, children: [] }], [], true)}
                                {buildTitleList(1, dataList.children || [], [], false, { blockType: displayBlockType, id: labelData.id }, displayBlockType === "label_group" ? labelData.color : "")}
                              </div>
                              <div
                                style={{
                                  width: `${CHARGE_WIDTH}px`,
                                  height: `${(listCount * 39) + 1}px`,
                                  display: "flex",
                                  flexFlow: "column",
                                  backgroundColor: "#FFFFFF",
                                  marginLeft: "-1px",
                                }}
                              >
                                <div
                                  style={{
                                    padding: "4px",
                                    textAlign: "center",
                                    fontWeight: "bold",
                                    minHeight: "40px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    userSelect: "none",
                                  }}
                                >
                                  担当者
                                </div>
                                {buildChargeList([dataList])}
                              </div>
                              {
                                isDisplayStatus && (
                                  <>
                                    <div
                                      style={{
                                        width: `${statusWidth}px`,
                                        height: `${(listCount * 39) + 1}px`,
                                        display: "flex",
                                        flexFlow: "column",
                                        backgroundColor: "#FFFFFF",
                                        marginLeft: "-1px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          padding: "4px",
                                          textAlign: "center",
                                          fontWeight: "bold",
                                          minHeight: "40px",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          userSelect: "none",
                                        }}
                                      >
                                        ステータス
                                      </div>
                                      {buildStatusList([dataList])}
                                    </div>
                                  </>
                                )
                              }
                              <div
                                style={{
                                  width: `calc(100% - ${titleWidth + CHARGE_WIDTH + statusWidth}px)`,
                                  overflowX: "scroll",
                                  scrollbarWidth: "none",
                                  position: "relative",
                                  marginLeft: "-1px",
                                }}
                              >
                                <div
                                  style={{
                                    position: "relative",
                                    width: "fit-content",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: "1px",
                                      minHeight: "40px",
                                      fontWeight: "bold",
                                      fontSize: "10px",
                                    }}
                                  >
                                    {
                                      displayDateList.map((v, i) => (
                                        <div
                                          key={`${dataList.name}_date_list_${v}`}
                                          style={{
                                            color: isDisplayWeekly ? "#666666" : calendarColor(v),
                                            width: `${isDisplayWeekly ? WEEKLY_BLOCK_WIDTH : DAILY_BLOCK_WIDTH}px`,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            padding: "8px 4px",
                                            whiteSpace: "nowrap",
                                            userSelect: "none",
                                          }}
                                        >
                                          {
                                            isDisplayWeekly ? (
                                              <>
                                                {`${format(v, "M/D")} - ${dateManipulateDay(v, 6, "M/D")}`}
                                              </>
                                            ) : (
                                              <>
                                                {
                                                  !i || Number(format(v, "D")) === 1 ? (
                                                    <>
                                                      {format(v, "M/D")}
                                                    </>
                                                  ) : (
                                                    <>
                                                      {format(v, "D")}
                                                    </>
                                                  )
                                                }
                                              </>
                                            )
                                          }
                                        </div>
                                      ))
                                    }
                                  </div>
                                  <div
                                    style={{
                                      position: "relative",
                                      width: `${(displayDateList.length * ((isDisplayWeekly ? WEEKLY_BLOCK_WIDTH : DAILY_BLOCK_WIDTH) + 1) + 1)}px`,
                                      height: `${(listCount * 39) + 1}px`,
                                      border: "solid 1px #CCCCCC",
                                      marginTop: "-1px",
                                    }}
                                  >
                                    {
                                      displayDateList.map((v, i) => (
                                        <React.Fragment key={`${dataList.name}_date_${v}`}>
                                          <div
                                            style={{
                                              position: "absolute",
                                              left: `${((isDisplayWeekly ? WEEKLY_BLOCK_WIDTH : DAILY_BLOCK_WIDTH) + 1) * i}px`,
                                              width: "1px",
                                              height: `${listCount * 39}px`,
                                              backgroundColor: "#DDDDDD",
                                              zIndex: 1,
                                            }}
                                          />
                                          {
                                            !isDisplayWeekly && calendarColor(v) !== "#666666" && (
                                              <div
                                                style={{
                                                  position: "absolute",
                                                  left: `${((isDisplayWeekly ? WEEKLY_BLOCK_WIDTH : DAILY_BLOCK_WIDTH) + 1) * i}px`,
                                                  width: `${((isDisplayWeekly ? WEEKLY_BLOCK_WIDTH : DAILY_BLOCK_WIDTH) + 1)}px`,
                                                  height: `${listCount * 39}px`,
                                                  backgroundColor: `${calendarColor(v)}0d`,
                                                  pointerEvents: "none",
                                                  zIndex: 1,
                                                }}
                                              />
                                            )
                                          }
                                        </React.Fragment>
                                      ))
                                    }
                                    {buildScheduleList([{ ...dataList, block_type: displayBlockType, assigned_members: [], children: [], status: null }])}
                                    {buildScheduleList(dataList.children, { blockType: displayBlockType, id: labelData.id, color: labelData.color }, displayBlockType === "label_group" ? labelData.color : "")}
                                    {todayBorder()}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </React.Fragment>
                        ) : (
                          <>
                            <div
                              style={{
                                textAlign: "center",
                                fontSize: "30px",
                                color: "#CCCCCC",
                              }}
                            >
                              対象のデータがありませんでした
                            </div>
                          </>
                        )
                      }
                    </>
                  )
                }
              </div>
            </>
          )
        }
      </div>
    </>
  )
}

GanttChart.defaultProps = {
  open: false,
}

export default GanttChart
